import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMutation, useQuery } from '@apollo/client';
import { Alert, Card, CardContent, CheckboxInput, Spacer, TextInput, } from '@guildeducationinc/recess';
import EditIcon from '@guildeducationinc/recess/icons/EditIcon';
import { AdminTopNav } from '@src/components/Admin/AdminTopNav';
import { CountrySelect } from '@src/components/CountrySelect';
import { COUNTRIES } from '@src/graphql/queries/lookup';
import { SAVE_PROGRAM_LEVEL, UPDATE_PROGRAM_LEVELS } from '@src/graphql/queries/programLevel';
import { useAuth } from '@src/hooks/useAuth';
import { useProgramLevels } from '@src/hooks/useProgramLevels';
import { prepareForSave } from '@src/utilities/gqlHelper';
import { pick, sortBy } from 'lodash';
import { DataTable, Form, getLanguageDisplayName, MuiMultiSelect, SearchFilterBar, TrackableButton, useAlert, useContentItems, useLocale, useSessionState, } from 'pe-core-frontend';
import { useEffect, useMemo, useState } from 'react';
import styles from './ProgramLevels.module.scss';
export const ProgramLevels = () => {
    const { currentUser } = useAuth();
    const { t } = useLocale();
    const { showAlert } = useAlert();
    const [searchText, setSearchText] = useSessionState('programLevelsSearch', '');
    const [countryCode, setCountryCode] = useSessionState('programLevelsCountryFilter', null);
    const [showActive, setShowActive] = useSessionState('programLevelsShowActive', true);
    const [showInactive, setShowInactive] = useSessionState('programLevelsShowInactive', true);
    // Determine is_active filter based on checkbox states
    const isActiveFilter = showActive && showInactive ? undefined : showActive ? true : showInactive ? false : undefined;
    const { programLevels, loading, error, page, pageSize, setPage, setPageSize, total, orderBy, orderByDir, setOrderBy, setOrderByDir, refetch, } = useProgramLevels({
        searchText,
        countryCode,
        isActive: isActiveFilter,
    });
    const [saveProgramLevel] = useMutation(SAVE_PROGRAM_LEVEL);
    const [updateProgramLevels] = useMutation(UPDATE_PROGRAM_LEVELS);
    const [selectedProgramLevel, setSelectedProgramLevel] = useState(null);
    const contentItemsData = useContentItems({
        clientId: currentUser?.clientId,
        code: selectedProgramLevel?.content?.code,
    });
    const [contentItems, setContentItems] = useState(contentItemsData?.items || []);
    useEffect(() => {
        if (!selectedProgramLevel || !selectedProgramLevel.id)
            return;
        setContentItems(contentItemsData.items || []);
    }, [selectedProgramLevel, contentItemsData.items]);
    const formFields = useMemo(() => {
        return [
            {
                name: 'name',
                label: t('Name'),
            },
            {
                name: 'isActive',
                label: t('Active?'),
                type: 'boolean',
                serializeValue: (value) => (value ? 'true' : 'false'),
                deserializeValue: (value) => value === 'true',
            },
        ];
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const handleValidate = (programLevel) => {
        const errors = {};
        if (!programLevel.name) {
            errors.name = t('{field} is required', { values: { field: t('Name') } });
        }
        if (!contentItems || contentItems.length === 0) {
            errors.content = t('{field} is required', { values: { field: t('Content') } });
        }
        else {
            const enItem = contentItems.find(item => item.language === 'en');
            if (!enItem || !enItem.value?.trim()) {
                errors.content = t('English content is required');
            }
        }
        return errors;
    };
    // Get Country Options
    const { data: countriesData, loading: countriesLoading, error: countriesError, } = useQuery(COUNTRIES, {
        variables: { includeInactive: false },
    });
    const countryOptions = countriesData?.countries.map((c) => ({
        id: c.params.id,
        code: c.value,
        name: c.label,
    }));
    // Build options from lookup; ensure selected countries appear even if not in lookup
    const { combinedCountryOptions, countryByIdMap } = useMemo(() => {
        const raw = (countryOptions || []).map((o) => ({
            value: o.id,
            label: o.name,
        }));
        const optionsMap = new Map();
        raw.forEach(o => optionsMap.set(o.value, o));
        const selected = selectedProgramLevel?.countries || [];
        selected.forEach(c => {
            if (!optionsMap.has(c.id)) {
                optionsMap.set(c.id, { value: c.id, label: c.name });
            }
        });
        const byId = new Map();
        selected.forEach(c => byId.set(c.id, c));
        optionsMap.forEach((opt, id) => {
            if (!byId.has(id)) {
                byId.set(id, { id, name: opt.label, code: '' });
            }
        });
        return {
            combinedCountryOptions: Array.from(optionsMap.values()),
            countryByIdMap: byId,
        };
    }, [countryOptions, selectedProgramLevel]);
    const handleSave = async (programLevel) => {
        try {
            const result = await saveProgramLevel({
                variables: {
                    input: {
                        ...pick(prepareForSave(programLevel), 'name', 'isActive', 'contentId'),
                        id: programLevel.id ? programLevel.id : null,
                        contentItems: contentItems.map(ci => ({
                            ...prepareForSave(ci),
                        })),
                        countryIds: programLevel.countries?.map(c => c.id),
                    },
                },
            });
            if (result.data?.saveProgramLevel && !result.data.saveProgramLevel.success) {
                return result.data.saveProgramLevel;
            }
            await refetch();
            setSelectedProgramLevel(null);
            const message = result.data.saveProgramLevel.messages[0];
            const toShow = t(message.value, { values: message.params });
            showAlert(toShow, 'success');
        }
        catch (err) {
            // eslint-disable-next-line no-console
            console.error(err);
            throw new Error(t('Unable to save program level'));
        }
    };
    const handleSortModelChange = (sortModel) => {
        if (sortModel && sortModel.length > 0) {
            const sort = sortModel[0];
            setOrderBy(sort.field);
            setOrderByDir(sort.sort);
        }
        else {
            setOrderBy(null);
            setOrderByDir(null);
        }
    };
    const handleContentItemChange = (contentItem) => (e) => {
        setContentItems(prev => {
            const newContentItems = prev.map(ci => {
                if (ci.language === contentItem.language) {
                    return {
                        ...ci,
                        value: e.target.value,
                    };
                }
                return ci;
            });
            return newContentItems;
        });
    };
    const handleUpdateSearchResult = async (isActive) => {
        try {
            const result = await updateProgramLevels({
                variables: {
                    input: { isActive },
                    spec: {
                        name: searchText || undefined,
                        countryCode: countryCode || undefined,
                    },
                },
            });
            if (result.data?.updateProgramLevels && !result.data.updateProgramLevels.success) {
                return result.data.updateProgramLevels;
            }
            await refetch();
            const message = result.data?.updateProgramLevels.messages[0];
            if (message) {
                showAlert(message.value, 'success');
            }
        }
        catch (err) {
            // eslint-disable-next-line no-console
            console.error(err);
            showAlert(t('Unable to update program levels'), 'error');
        }
    };
    return (_jsxs("div", { children: [_jsx(AdminTopNav, { title: t('Program levels'), searchFilterActions: _jsx(SearchFilterBar, { enableSearch: true, enableFilter: true, searchText: searchText, onSearchChange: text => {
                        setSearchText(text);
                        setPage(1);
                    }, searchPlaceholder: t('Search by Name'), filterActions: _jsxs("div", { className: styles.filterActions, children: [_jsxs("div", { className: styles.filterGroup, children: [_jsx(CheckboxInput, { label: t('Active'), checked: showActive, onChange: e => {
                                            setShowActive(e.target.checked);
                                            setPage(1);
                                        } }), _jsx(CheckboxInput, { label: t('Inactive'), checked: showInactive, onChange: e => {
                                            setShowInactive(e.target.checked);
                                            setPage(1);
                                        } })] }), _jsx(CountrySelect, { label: t('Country'), className: styles.countrySelect, value: countryCode || '', onChange: code => {
                                    setCountryCode(code || null);
                                    setPage(1);
                                }, supportEmptyState: true, options: sortBy(countryOptions || [], 'name').map((c) => ({
                                    value: c.code,
                                    label: c.name,
                                })) })] }), isFilterActive: !!countryCode || !showActive || !showInactive }) }), _jsx("div", { children: _jsx(Card, { children: _jsxs(CardContent, { children: [error && (_jsx(Alert, { display: true, hideClose: true, type: 'danger', children: error.message })), total > 0 && !loading && (_jsxs("div", { className: 'mb-3 flex gap-1', children: [_jsx(TrackableButton, { btnType: 'mui', enText: 'Make Inactive', tag: 'make_inactive', size: 'small', variant: 'outlined', onClick: () => handleUpdateSearchResult(false), children: `Make ${total} Inactive` }), _jsx(TrackableButton, { btnType: 'mui', enText: 'Make Active', tag: 'make_active', size: 'small', variant: 'outlined', onClick: () => handleUpdateSearchResult(true), children: `Make ${total} Active` })] })), _jsx(DataTable, { disableRowSelectionOnClick: true, disableColumnMenu: true, rows: programLevels, loading: loading, pageSizeOptions: [10, 25], paginationMode: 'server', sortingMode: 'server', paginationModel: { page: page - 1, pageSize }, onPaginationModelChange: pagination => {
                                    setPage(pagination.page + 1);
                                    setPageSize(pagination.pageSize);
                                }, onSortModelChange: handleSortModelChange, sortModel: orderBy && orderByDir ? [{ field: orderBy, sort: orderByDir }] : [], rowCount: total, columns: [
                                    {
                                        field: 'name',
                                        headerName: t('Name'),
                                        flex: 0.3,
                                    },
                                    {
                                        field: 'numCountries',
                                        headerName: t('Num Countries'),
                                        width: 150,
                                        valueGetter: params => params.row.countries?.length || 0,
                                        sortable: true,
                                    },
                                    {
                                        field: 'countries',
                                        headerName: t('Countries'),
                                        flex: 0.3,
                                        sortable: false,
                                        renderCell: params => {
                                            const countries = params.row.countries || [];
                                            const names = countries.map((c) => c.name);
                                            const shown = names.slice(0, 5).join(', ');
                                            const suffix = names.length > 5 ? ', ...' : '';
                                            return `${shown}${suffix}`;
                                        },
                                    },
                                    {
                                        field: 'isActive',
                                        headerName: t('Active?'),
                                        width: 100,
                                        renderCell: params => (params.value ? t('Yes') : t('No')),
                                    },
                                    {
                                        field: 'actions',
                                        headerName: t('Actions'),
                                        width: 100,
                                        renderCell: ({ row }) => (_jsx("div", { children: _jsx(TrackableButton, { btnType: 'icon', enText: 'Edit', tag: `program_level_${row.id}`, appearance: 'minimal', label: t('Edit'), icon: _jsx(EditIcon, { size: 20 }), onClick: () => {
                                                    setSelectedProgramLevel(row);
                                                } }) })),
                                    },
                                ] })] }) }) }), selectedProgramLevel && (_jsx(Form, { fields: formFields, title: selectedProgramLevel.id ? 'Edit program level' : 'Add program level', modal: true, obj: selectedProgramLevel, onValidate: handleValidate, onSave: handleSave, minHeight: 150, onCancel: () => setSelectedProgramLevel(null), children: formObj => (_jsxs("div", { className: 'flex flex-col gap-3', children: [sortBy(contentItems, it => getLanguageDisplayName(it.language)).map(contentItem => (_jsxs("div", { children: [_jsx("h4", { className: 'text-2xl', children: getLanguageDisplayName(contentItem.language) }), _jsx(TextInput, { label: t('Name'), hideLabel: true, className: 'max-w-full', value: contentItem.value || '', onChange: handleContentItemChange(contentItem), errors: formObj.errors?.content })] }, contentItem.language))), _jsx(Spacer, { margin: 8 }), combinedCountryOptions.length > 0 && !countriesError ? (_jsx(MuiMultiSelect, { label: t('Countries'), options: combinedCountryOptions, value: formObj.draft?.countries?.map(c => c.id) || [], onChange: event => {
                                const selectedIds = event.selection?.map(x => x.value) ?? [];
                                formObj.setDraft?.((draft) => ({
                                    ...draft,
                                    countries: selectedIds
                                        .map(id => countryByIdMap.get(id))
                                        .filter((c) => Boolean(c)),
                                }));
                            }, disabled: countriesLoading })) : (_jsx("div", { children: t('There are no active countries to select from. Please Add a Country.') }))] })) }))] }));
};
